<template>
  <v-container fluid>
    <div id="content">

      <section class="d-flex">
        <BreadCrumb :items="$route.matched"></BreadCrumb>
      </section>

      <section>
        <h3>予約対象を選択</h3>
        <v-row>
          <v-col cols="6" md="3">
            <v-combobox
              ref="group"
              label="施設グループ"
              v-model="filterKeyword.group"
              :items="groups"
              append-icon="fas fa-list-ol"
              @click.stop="openSelected('group')"
              @change="groupChange"
              readonly outlined hide-details="auto">
            </v-combobox>
          </v-col>
          <v-col cols="6" md="3">
            <v-combobox
              ref="facility"
              label="施設"
              v-model="filterKeyword.facility"
              :items="facilities"
              append-icon="fas fa-list-ol"
              @click.stop="openSelected('facility')"
              @change="facilityChange"
              readonly outlined hide-details="auto">
            </v-combobox>
          </v-col>
        </v-row>
      </section>

      <section v-if="filterKeyword.facility">
        <h3 class="mt-5">予約状況</h3>
        <v-sheet class="d-flex justify-space-between">
          <v-btn icon class="ma-2" @click="$refs.calendar.prev()">
            <v-icon>fas fa-chevron-left</v-icon>
          </v-btn>

          <v-menu
            v-model="viewDate"
            :close-on-content-click="false"
            :nudge-right="0"
            transition="scale-transition" offset-y min-width="290px">
            <template v-slot:activator="{ on }">
              <v-btn text class="ma-2" v-if="$refs.calendar" v-on="on">
                {{ calendarTitle }}
              </v-btn>
            </template>
            <v-date-picker v-model="calendar" @input="viewDate = false"></v-date-picker>
          </v-menu>
          
          <v-btn icon class="ma-2" @click="$refs.calendar.next()">
            <v-icon>fas fa-chevron-right</v-icon>
          </v-btn>
        </v-sheet>
        <v-sheet :height="calendarHeight">
          <v-calendar
            ref="calendar"
            v-model="calendar"
            :type="calendarType"
            :first-interval="firstInterval"
            :interval-count="intervalCount"
            :categories="facilities"
            :events="customEvents"
            :show-month-on-first="false"
            :day-format="view => dayFormat(view.date)"
            @change="viewChange"
            @mousedown:event="clickEvent"
            @mousedown:time="mouseStart"
            @mousemove:time="mouseMove"
            @mousemove:day="mouseCancel"
            @mouseup:time="mouseEnd"
            @mouseleave.native="mouseCancel"
            @touchstart:event="touchEvent"
            @touchmove:event="touchMove"
            @touchstart:time="mouseStart"
            @touchmove:time="mouseMove"
            @touchend:time="mouseEnd">
            <template v-slot:interval="{ weekday }">
              <div :class="['interval-content', isHoliday(weekday) ? 'disabled' : '']"></div>
            </template>
            <template v-slot:event="{ event }">
              <div class="event-content">
                <span>
                  <i class="far fa-calendar"></i>
                  {{ event.name }}
                </span>
                <template v-if="event.customerID">
                  <span>
                    <i class="fas fa-user"></i>
                    {{ getCustomer(event.customerID) }}
                  </span>
                </template>
                <span>
                  <i class="far fa-clock"></i>
                  {{ getTime(event.start) }}～{{ getTime(event.end) }}
                </span>
                <template v-if="event.charge">
                  <span>
                    <i class="fas fa-edit"></i>
                    {{ event.charge }}
                  </span>
                </template>
                <template v-if="event.remarks">
                  <span>
                    <i class="far fa-comment-alt"></i>
                    {{ event.remarks }}
                  </span>
                </template>
              </div>
            </template>
          </v-calendar>
        </v-sheet>
      </section>

      <!-- 編集ダイアログ -->
      <v-navigation-drawer v-model="dialog" fixed temporary right stateless width="600" id="dialog" class="edit-dialog">
        <v-card flat>
          <v-card-title>
            <div class="title">{{ $route.name }}を{{ this.editedIndex === -1 ? '追加' : '編集' }}</div>
          </v-card-title>
          <v-card-text>
            <v-form v-model="valid">
              <v-row no-gutters>
                <v-col cols="12" class="pa-2">
                  <v-text-field
                    ref="name"
                    label="タイトル"
                    v-model="editedItem.name"
                    :rules="[rules.required]"
                    class="required"
                    outlined hide-details="auto">
                  </v-text-field>
                </v-col>
                <v-col cols="12" class="pa-2">
                  <v-combobox
                    ref="customerID"
                    label="客先"
                    v-model="editedItem.customerID"
                    :items="customerList"
                    append-icon="fas fa-list-ol"
                    :rules="[rules.required]"
                    class="required"
                    @click.stop="openSelected('customerID')"
                    readonly clearable outlined hide-details="auto">
                  </v-combobox>
                </v-col>
                <v-col cols="6" class="pa-2">
                  <v-menu
                    v-model="startDate"
                    :close-on-content-click="false"
                    :nudge-right="0"
                    transition="scale-transition" offset-y min-width="290px">
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        ref="startDate"
                        label="開始日付"
                        v-model="editedItem.startDate"
                        :rules="[rules.required, enableday, sameday]"
                        class="required"
                        v-on="on"
                        outlined hide-details="auto">
                      </v-text-field>
                    </template>
                    <v-date-picker v-model="editedItem.startDate" @input="startDate = false"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="6" class="pa-2">
                  <v-text-field
                    ref="startTime"
                    label="開始時刻"
                    type="time"
                    v-model="editedItem.startTime"
                    :rules="[rules.required]"
                    class="required"
                    :step="step"
                    outlined hide-details="auto">
                  </v-text-field>
                </v-col>
                <v-col cols="6" class="pa-2">
                  <v-menu
                    v-model="endDate"
                    :close-on-content-click="false"
                    :nudge-right="0"
                    transition="scale-transition" offset-y min-width="290px">
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        ref="endDate"
                        label="終了日付"
                        v-model="editedItem.endDate"
                        :rules="[rules.required, sameday]"
                        class="required"
                        v-on="on"
                        outlined hide-details="auto">
                      </v-text-field>
                    </template>
                    <v-date-picker v-model="editedItem.endDate" @input="endDate = false"></v-date-picker>
                  </v-menu>
                </v-col>
                <v-col cols="6" class="pa-2">
                  <v-text-field
                    ref="endTime"
                    label="終了時刻"
                    type="time"
                    v-model="editedItem.endTime"
                    :rules="[rules.required]"
                    class="required"
                    :step="step"
                    outlined hide-details="auto">
                  </v-text-field>
                </v-col>
                <v-col cols="6" class="pa-2">
                  <v-text-field
                    ref="charge"
                    label="担当"
                    v-model="editedItem.charge"
                    outlined hide-details="auto">
                  </v-text-field>
                </v-col>
                <v-col cols="6" class="pa-2">
                  <v-swatches v-model="editedItem.color">
                    <v-text-field
                      ref="color"
                      label="色"
                      slot="trigger"
                      :background-color="editedItem.color"
                      v-model="editedItem.color"
                      readonly outlined hide-details="auto">
                    </v-text-field>
                  </v-swatches>
                </v-col>
                <v-col cols="12" class="pa-2">
                  <v-textarea
                    ref="remarks"
                    label="備考"
                    v-model="editedItem.remarks"
                    rows="5"
                    outlined hide-details="auto">
                  </v-textarea>
                </v-col>
              </v-row>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn color="primary" @click.stop="save" :loading="saving" :disabled="!valid" text>
              <v-icon left>fas fa-save</v-icon>{{ this.editedIndex === -1 ? '登録' : '更新' }}
            </v-btn>
            <v-btn color="error" @click.stop="remove" :loading="saving" v-if="editedIndex > -1" text>
              <v-icon left>fas fa-trash-alt</v-icon>削除
            </v-btn>
            <v-btn @click="dialog = false" text>
              <v-icon left>fas fa-times</v-icon>閉じる
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-navigation-drawer>

    </div>
  </v-container>
</template>

<script>
import { API, graphqlOperation } from 'aws-amplify'
import { createFacilityReserve, updateFacilityReserve, deleteFacilityReserve } from '../graphql/mutations'
import { listFacilities, listFacilityReserves, listCustomers } from '../graphql/queries'
import { 
  onCreateFacility, onUpdateFacility, onDeleteFacility,
  onCreateFacilityReserve, onUpdateFacilityReserve, onDeleteFacilityReserve } from '../graphql/subscriptions'
import moment from 'moment'
import VSwatches from 'vue-swatches'
import 'vue-swatches/dist/vue-swatches.css'

export default {
  data() {
    return {
      loading: true,
      saving: false,
      dialog: false,
      valid: false,
      desserts: [],
      events: [],
      customers: [],
      editedIndex: -1,
      editedItem: {},
      calendar: moment().format('YYYY-MM-DD'),
      filterKeyword: {},
      editEvent: false,
      createEvent: null,
      createStart: null,
      startDate: false,
      endDate: false,
      viewDate: false,
      hookFlg: false,
    }
  },
  prop: {},
  mounted: async function() {
    /*---------------------------
      * 施設
      ---------------------------*/
    await this.getFacilities()
    //作成時 subscription
    await API.graphql(graphqlOperation(onCreateFacility)).subscribe({
      next: (e) => {
        const data = e.value.data.onCreateFacility
        this.desserts.push(data)
      }
    })
    //更新時 subscription
    await API.graphql(graphqlOperation(onUpdateFacility)).subscribe({
      next: (e) => {
        const data = e.value.data.onUpdateFacility
        this.desserts.find(origin => {
          if (origin.id === data.id) {
            origin = Object.assign(origin, data)
          }
        })
      }
    })
    //削除時 subscription
    await API.graphql(graphqlOperation(onDeleteFacility)).subscribe({
      next: (e) => {
        const data = e.value.data.onDeleteFacility
        this.desserts = this.desserts.filter(origin => origin.id !== data.id)
      }
    })
    /*---------------------------
    * 予約情報
    ---------------------------*/
    //作成時
    await API.graphql(graphqlOperation(onCreateFacilityReserve)).subscribe({
      next: (e) => {
        const row = e.value.data.onCreateFacilityReserve
        this.events.push(row)
      }
    })
    //更新時
    await API.graphql(graphqlOperation(onUpdateFacilityReserve)).subscribe({
      next: (e) => {
        const row = e.value.data.onUpdateFacilityReserve
        this.events.find(origin => {
          if (origin.id === row.id) {
            origin = Object.assign(origin, row)
          }
        })
      }
    })
    //削除時 
    await API.graphql(graphqlOperation(onDeleteFacilityReserve)).subscribe({
      next: (e) => {
        const row = e.value.data.onDeleteFacilityReserve
        this.events = this.events.filter(origin => origin.id !== row.id)
      }
    })
    //客先 query
    await this.getCustomers()

    this.loading = false
  },
  beforeDestroy() {},
  created() {
    if (!this.isAllowed(this.$route.name)) this.$router.push('/')
  },
  computed: {
    //施設グループ一覧
    groups() {
      const map = []
      for (const facility of this.desserts) {
        if (!map.find(e => e === facility.group)) {
          map.push(facility.group)
        }
      }
      return map
    },
    //施設一覧
    facilities() {
      const map = []
      for (const facility of this.desserts) {
        if (facility.group == this.filterKeyword.group) {
          map.push(Object.assign({
            value: facility.id,
            text: facility.name,
          }, facility))
        }
      }

      return map
    },
    //客先一覧
    customerList() {
      const map = []
      this.customers.forEach(e => {
        map.push({
          value: e.id,
          text: e.name + (e.office ? '（' + e.office + '）' : ''),
        })
      })

      return map
    },
    //イベント一覧（カスタム）
    customEvents() {
      const map = []

      for (const event of this.events) {
        const custom = Object.assign({}, event)
        custom.color = this.getCustomerColor(event.customerID, event.color)
        map.push(custom)
      }

      return map
    },
    //カレンダーのタイトル
    calendarTitle() {
      return moment(this.calendar).format('YYYY年 M月')
    },
    //カレンダーの高さ
    calendarHeight() {
      switch (this.filterKeyword.facility.type) {
        case '時間':
          return null
        default:
          return 500
      }
    },
    //カレンダーのタイプ
    calendarType() {
      switch (this.filterKeyword.facility.type) {
        case '時間':
          return 'week'
        default:
          return 'month'
      }
    },
    //開始時間
    firstInterval() {
      if (this.filterKeyword.facility.enableTime) {
        const time = this.filterKeyword.facility.enableTime[0]
        return Math.floor(time / 60)

      } else {
        return null
      }
    },
    //表示期間
    intervalCount() {
      if (this.filterKeyword.facility.enableTime) {
        const from = this.filterKeyword.facility.enableTime[0]
        const to = this.filterKeyword.facility.enableTime[1]

        return Math.floor((to - from) / 60)

      } else {
        return null
      }
    },
    //最小選択時間
    step() {
      if (this.filterKeyword.facility && this.filterKeyword.facility.minMinutes) {
        return this.filterKeyword.facility.minMinutes * 60
      } else {
        return 60
      }
    },
    //
    form() {
      return {
        id: this.editedItem.id || null,
        facilityID: this.filterKeyword.facility.id,
        customerID: this.editedItem.customerID ? this.editedItem.customerID.value: null,
        name: this.editedItem.name || null,
        start: this.editedItem.startDate + ' ' + this.editedItem.startTime,
        end: this.editedItem.endDate + ' ' + this.editedItem.endTime,
        charge: this.editedItem.charge || null,
        color: this.editedItem.color,
        remarks: this.editedItem.remarks || null,
      }
    },
    //同日チェック
    sameday() {
      if (this.editedItem.startDate && this.editedItem.endDate) {
        return (this.editedItem.startDate == this.editedItem.endDate) || '日を跨いだ予約は指定できません'
      } else {
        return false
      }
    },
    //予約可能日チェック
    enableday() {
      if (this.editedItem.startDate) {
        let weekday = moment(this.editedItem.startDate).day()
        return !this.isHoliday(weekday) || '休日は指定できません'
      } else {
        return false
      }
    },
  },
  watch: {
    dialog(val) {
      val || this.close()
    },
  },
  methods: {
    //初期化
    async init() {

    },
    //保存
    async save() {
      try {
        this.saving = true
        const data = Object.assign({}, this.form)
        //ブッキングチェック
        for (const event of this.events) {
          //選択中のイベントは比較対象から省く
          if (this.createEvent && this.createEvent == event || data.id == event.id) continue
          //他のイベントとブッキングしないか
          //((#start < :start AND :start < #end) OR (#start < :end AND :end < #end) OR (:start < #start AND #start < :end))
          if (moment(data.start) > moment(event.start) && moment(data.start) < moment(event.end) || 
              moment(data.end) > moment(event.start) && moment(data.end) < moment(event.end) ||
              moment(data.start) < moment(event.start) && moment(data.end) > moment(event.start)) {
            throw { message: '予約がブッキングしているため、この日時では登録できません' }
          }
        }
        //編集
        if (this.editedIndex >= 0) {
          await API.graphql(graphqlOperation(updateFacilityReserve, {input: data}))
          this.log({ type: this.$route.name, text: '[' + data.name + '] を更新しました' })
        //新規
        } else {
          delete data.id
          await API.graphql(graphqlOperation(createFacilityReserve, {input: data}))
          this.log({ type: this.$route.name, text: '[' + data.name + '] を追加しました' })
        }
        this.dialog = false

      } catch (error) {
        this.myAlert(error.message)
        this.saving = false
      }
    },
    //削除
    async remove() {
      try {
        if (confirm(this.editedItem.name + 'を削除しますか？')) {
          //削除
          await API.graphql(graphqlOperation(deleteFacilityReserve, {
            input: { id: this.editedItem.id }
          }))
          this.log({ type: this.$route.name, text: '[' + this.editedItem.name + '] を削除しました' })
          this.myAlert(this.editedItem.name + 'を削除しました', 'success')
          this.dialog = false
        }
      } catch (error) {
        this.myAlert(error)
      }
    },
    //ダイアログclose
    close() {
      this.saving = false
      this.editEvent = false
      this.mouseCancel()
      setTimeout(() => {
        this.resetForm()
        this.editedIndex = -1
      }, 300)
    },
    //フォームreset
    resetForm() {
      Object.keys(this.form).forEach(f => {
        if (this.$refs[f]) this.$refs[f].reset()
      })
      this.editedItem.id = null
    },
    //イベントクリック
    clickEvent({ event }) {
      this.editEvent = true
      //新しいイベントの登録ダイアログを表示
      const target = this.events.find(e => e.id === event.id)
      this.editedIndex = this.events.findIndex(e => e.id === event.id)
      this.editedItem = {
        id: target.id,
        name: target.name,
        customerID: target.customerID ? this.customerList.find(e => e.value == target.customerID) : null,
        startDate: moment(target.start).format('YYYY-MM-DD'),
        startTime: moment(target.start).format('HH:mm'),
        endDate: moment(target.end).format('YYYY-MM-DD'),
        endTime: moment(target.end).format('HH:mm'),
        charge: target.charge || null,
        color: target.color,
        remarks: target.remarks || null,
      }
      this.dialog = true
    },
    //イベントタッチ
    touchEvent(info) {
      this.editEvent = true
      setTimeout(function(path) {
        if (path.editEvent) path.clickEvent(info)
      }, 500, this)
    },
    //イベントタッチして移動
    touchMove() {
      this.editEvent = false
    },
    //タイムテーブル タップ
    mouseStart(view) {
      //休日は作成不可
      if (this.isHoliday(view.weekday)) return

      if (!this.editEvent) {
        this.hookFlg = true
        setTimeout(function(path) {
          if (path.hookFlg) path.hookEvent(view)
        }, 500, this)
      }
    },
    //タイムテーブル 新しいイベント生成
    hookEvent(view) {
      const mouse = this.toTime(view)
      this.createStart = this.roundTime(mouse)

      this.createEvent = {
        name: '選択中',
        start: this.createStart,
        end: this.createStart,
        color: 'blue lighten-4',
        timed: true,
      }
      this.events.push(this.createEvent)
    },
    //タイムテーブル ドラッグ中
    mouseMove(view) {
      this.hookFlg = false
      //休日または日を跨ぐ場合は作成不可
      if (this.isHoliday(view.weekday) || 
          moment(this.createStart).format('YYYY-MM-DD') != moment(view.date).format('YYYY-MM-DD')) return
      const mouse = this.toTime(view)
      if (this.createEvent && this.createStart) {
        const mouseRounded = this.roundTime(mouse, false)
        const min = Math.min(mouseRounded, this.createStart)
        const max = Math.max(mouseRounded, this.createStart)

        this.createEvent.start = min
        this.createEvent.end = max
      }
    },
    //タイムテーブル ドラッグ終了
    mouseEnd() {
      this.hookFlg = false
      if (this.createEvent && this.createStart) {
        //新しいイベントの登録ダイアログを表示
        this.editedItem = {
          name: null,
          startDate: moment(this.createEvent.start).format('YYYY-MM-DD'),
          startTime: moment(this.createEvent.start).format('HH:mm'),
          endDate: moment(this.createEvent.end).format('YYYY-MM-DD'),
          endTime: moment(this.createEvent.end).format('HH:mm'),
          color: '#1FBC9C',
        }
        this.dialog = true
      }
    },
    //タイムテーブル ドラッグ範囲外
    mouseCancel() {
      if (this.dialog) return

      this.events = this.events.filter(e => e !== this.createEvent)

      this.createEvent = null
      this.createStart = null
    },
    //timestampへの変換
    toTime(view) {
      let dayStr = view.date
      if (view.time != '') dayStr += ' ' + view.time
      
      return moment(dayStr).valueOf()
    },
    //分の丸め
    roundTime(time, down = true) {
      const roundTo = this.filterKeyword.facility.minMinutes || 10
      const roundDownTime = roundTo * 60 * 1000

      return down
        ? time - time % roundDownTime
        : time + (roundDownTime - (time % roundDownTime))
    },
    //客先名を返す
    getCustomer(id) {
      const customer = this.customerList.find(e => e.value == id)

      return customer ? customer.text : null
    },
    //客先の色を返す
    getCustomerColor(id, defaultColor) {
      const customer = this.customers.find(e => e.id == id)
      let color = defaultColor
      
      if (customer && customer.color) color = customer.color

      return color
    },
    //時刻の表記を返す
    getTime(date) {
      return moment(date).format('H:mm')
    },
    //カレンダー 日付フォーマット
    dayFormat(date) {
      return moment(date).format('D')
    },
    //休日チェック
    isHoliday(weekday) {
      if (this.filterKeyword.facility && this.filterKeyword.facility.enableWeek) {
        return this.filterKeyword.facility.enableWeek.find(e => e == weekday) >= 0 ? false : true
      } else {
        return false
      }
    },
    //施設グループの変更
    groupChange() {
      this.$refs.facility.reset()
    },
    //施設の変更
    async facilityChange() {
      if (this.$refs.calendar) {
        this.events = []
        await this.getReserves(this.$refs.calendar.lastStart.date, this.$refs.calendar.lastEnd.date)
      }
    },
    //施設の取得
    async getFacilities(nextToken = null) {
      const query = await API.graphql(graphqlOperation(listFacilities, { limit: 500, nextToken: nextToken }))
      for (const data of query.data.listFacilities.items) {
        this.desserts.push(data)
      }
      if (query.data.listFacilities.nextToken) this.getFacilities(query.data.listFacilities.nextToken)
    },
    //ビュー変更
    async viewChange({start, end}) {
      this.events = []
      await this.getReserves(start.date, end.date)
    },
    //予約情報の取得
    async getReserves(start, end, nextToken = null) {
      const query = await API.graphql(graphqlOperation(listFacilityReserves, {
        filter: {
          and: [
            { facilityID: { eq: this.filterKeyword.facility.id } },
            { start: { ge: moment(start).subtract(1, 'day').format('YYYY-MM-DDTHH:mm:ss') } },
            { end: { le: moment(end).format('YYYY-MM-DDTHH:mm:ss') } },
          ],
        },
        limit: 500,
        nextToken: nextToken
      }))
      for (const data of query.data.listFacilityReserves.items) {
        this.events.push(data)
      }
      if (query.data.listFacilityReserves.nextToken) this.getReserves(start, end, query.data.listFacilityReserves.nextToken)
    },
    //客先の取得
    async getCustomers(nextToken = null) {
      const query = await API.graphql(graphqlOperation(listCustomers, { limit: 500, nextToken: nextToken }))
      for (const data of query.data.listCustomers.items) {
        this.customers.push(data)
      }
      if (query.data.listCustomers.nextToken) this.getCustomers(query.data.listCustomers.nextToken)
    },
  },
  components: {
    VSwatches,
  },
}
</script>

<style lang='scss' scoped>
.event-content {
  padding: 5px;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  span {
    display: block;
    i {
      margin-right: 5px;
    }
  }
}
.interval-content {
  height: 100%;
  &.disabled {
    background-color: #f2f2f2;
    cursor: not-allowed;
  }
}
</style>
